/**
 * BackNavigationDialog - Confirmation dialog for back navigation during critical steps.
 *
 * Shown when a user attempts to go back during image generation or refinement.
 * Warns that progress will be lost and requires explicit confirmation.
 *
 * Design: DENiZEN
 * - Soft borders (1-2px) with rounded corners
 * - Subtle shadows (shadow-denizen)
 * - Teal accent for warning
 * - WCAG 2.1 AA compliant (focus trapping, escape key, aria attributes)
 */

import { useEffect, useRef } from 'react'

export interface BackNavigationDialogProps {
  /** Whether the dialog is visible */
  isOpen: boolean
  /** The step the user is trying to leave */
  fromStep: string
  /** Whether image generation is actively running */
  isGenerating: boolean
  /** Callback when user confirms they want to go back */
  onConfirm: () => void
  /** Callback when user cancels and stays on the current step */
  onCancel: () => void
}

/**
 * Context-specific messages based on what the user is leaving.
 *
 * The 'generating' and 'generating_images' steps never reach the switch because
 * the `isGenerating` guard above always handles them first — the dialog is only
 * shown for those steps when generation is actively running.
 */
function getDialogContent(fromStep: string, isGenerating: boolean): {
  title: string
  message: string
  confirmLabel: string
  cancelLabel: string
} {
  if (isGenerating) {
    return {
      title: 'Generation in progress',
      message:
        'An image is currently being generated. If you go back now, the generation will be cancelled and you will need to start again.',
      confirmLabel: 'Cancel generation',
      cancelLabel: 'Keep waiting',
    }
  }

  switch (fromStep) {
    case 'image_refinement':
      return {
        title: 'Leave your images?',
        message:
          'If you go back, your generated images will be lost. You will return to the photo choice step and can start image generation again.',
        confirmLabel: 'Go back',
        cancelLabel: 'Stay here',
      }
    default:
      return {
        title: 'Go back?',
        message: 'Are you sure you want to go back? Any unsaved progress on this step may be lost.',
        confirmLabel: 'Go back',
        cancelLabel: 'Stay here',
      }
  }
}

export function BackNavigationDialog({
  isOpen,
  fromStep,
  isGenerating,
  onConfirm,
  onCancel,
}: BackNavigationDialogProps) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  // Focus the cancel button when dialog opens (safe default)
  useEffect(() => {
    if (!isOpen) return

    // Small delay to ensure the dialog is rendered before focusing
    const timer = setTimeout(() => {
      cancelButtonRef.current?.focus()
    }, 50)
    return () => clearTimeout(timer)
  }, [isOpen])

  // Combined keydown handler: Escape to dismiss + Tab/Shift+Tab focus trap (WCAG 2.1 AA)
  useEffect(() => {
    if (!isOpen) return

    const previouslyFocused = document.activeElement as HTMLElement | null
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel()
        return
      }

      if (e.key !== 'Tab') return

      // Use the ref to target this specific dialog, avoiding collisions
      // with other role="dialog" elements in the DOM (e.g. ImageSelectionModal)
      const dialog = dialogRef.current
      if (!dialog) return

      const focusable = dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
      previouslyFocused?.focus()
    }
  }, [isOpen, onCancel])

  if (!isOpen) return null

  const { title, message, confirmLabel, cancelLabel } = getDialogContent(fromStep, isGenerating)

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="back-nav-dialog-title"
      aria-describedby="back-nav-dialog-desc"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-dark-green/50"
        onClick={onCancel}
        aria-hidden="true"
      />

      {/* Dialog card */}
      <div
        className="relative bg-white border border-dark-green/15 p-6 max-w-md mx-4 animate-slide-up rounded-2xl shadow-denizen"
      >
        {/* Warning label */}
        <div className="pebble-label text-xs mb-4">
          WAIT!
        </div>

        {/* Warning icon */}
        <div className="w-12 h-12 bg-teal border border-dark-green/15 rounded-full flex items-center justify-center mb-4 shadow-denizen-sm">
          <svg
            className="w-6 h-6 text-white"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
              clipRule="evenodd"
            />
          </svg>
        </div>

        <h3
          id="back-nav-dialog-title"
          className="font-display font-bold text-xl text-dark-green tracking-tight mb-3"
        >
          {title}
        </h3>
        <p
          id="back-nav-dialog-desc"
          className="font-body text-sm text-muted mb-6 leading-relaxed"
        >
          {message}
        </p>

        <div className="flex gap-3">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onCancel}
            className="btn-denizen-primary flex-1 py-3 px-4"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="btn-denizen-secondary flex-1 py-3 px-4"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export default BackNavigationDialog
